/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import {
	getClientUniqueId,
	getQuestionIdFromLocalStorage,
	getSurveyUniqueId,
	isLoggedIn,
	setQuestionIdToLocalStorage
} from '../../../../core/localStorage';
import { useQuestionsData, useSurveyAnswer } from '../../../../core/api';
import SurveyAnswer, { createEmptySurveyAnswer } from '../../../../models/SurveyAnswer';
import { bbocWelcomePageUrl, questionPageUrlRegExp } from '../../../../core/constants';
import { getTheme } from '../../../../core/domain';
import { safeParseInt } from '../../../../lib/lib';
import NavigateWithAppName, { useNavigateWithAppName } from '../../../common/NavigateWithAppName';
import AutoLogoutLayout from '../../common/Layout';
import Helmet from '../../../../commonComponents/Helmet';
import QuestionHeader from './QuestionHeader';
import QuestionForm from './QuestionForm';

const QuestionPage = () => {
	const questionsData = useQuestionsData() ?? [];

	const navigate = useNavigateWithAppName();
	const clientUniqueIdInLocalStorage = getClientUniqueId();
	const surveyUniqueIdInLocalStorage = getSurveyUniqueId();
	if (!surveyUniqueIdInLocalStorage || !clientUniqueIdInLocalStorage)
		return <NavigateWithAppName to={bbocWelcomePageUrl} replace />;

	const location = useLocation();
	const questionId = getQuestionIdFromUrlOrLocalStorage(location);

	const questionSpec = questionsData[questionId - 1];
	const savedSurveyAnswer = useSurveyAnswer(surveyUniqueIdInLocalStorage, questionSpec?.uniqueName);
	const [surveyAnswer, setSurveyAnswer] = React.useState<SurveyAnswer>();

	React.useEffect(() => {
		if (savedSurveyAnswer !== undefined) {
			if (savedSurveyAnswer === '') {
				// API will return 204 '' if API gets null answer for the question.
				setSurveyAnswer(createEmptySurveyAnswer(questionSpec));
			} else {
				setSurveyAnswer(savedSurveyAnswer);
			}
		}
	}, [savedSurveyAnswer]);

	// questionId in url is the question index + 1 in questionData, so must be between 1 and questionData.length.
	if (questionId < 1 || (questionsData.length > 0 && questionId > questionsData.length))
		return <NavigateWithAppName to={bbocWelcomePageUrl} replace />;

	if (questionPageUrlRegExp.test(createFullUrl(location))) {
		const pathnameParts = location.pathname.split('/');
		pathnameParts.pop();
		const pathnameWithoutQuestionId = pathnameParts.join('/');
		return <NavigateWithAppName to={pathnameWithoutQuestionId} replace />;
	}

	const handleBackButtonClick = () => {
		navigate(bbocWelcomePageUrl);
	};

	return (
		<AutoLogoutLayout
			showFooter={false}
			headerText="Financial Progress Score"
			onBackButtonClick={isLoggedIn() ? handleBackButtonClick : null}
		>
			<Helmet>
				<title>Financial Progress Score</title>
			</Helmet>
			{questionSpec && (
				<ThemeProvider theme={getTheme(questionSpec.section.title) as any}>
					<QuestionHeader
						questionId={questionId}
						sectionTitle={questionSpec.section.title}
						introduction={questionSpec.section.introduction}
					/>
					{surveyAnswer && surveyAnswer?.questionUniqueName === questionSpec.uniqueName && (
						<QuestionForm
							questionId={questionId}
							surveyUniqueId={surveyUniqueIdInLocalStorage}
							surveyAnswer={surveyAnswer}
						/>
					)}
				</ThemeProvider>
			)}
		</AutoLogoutLayout>
	);
};

export default QuestionPage;

const getQuestionIdFromUrlOrLocalStorage = (location: any): number => {
	let questionId = getQuestionIdFromUrl(location);
	if (questionId) {
		setQuestionIdToLocalStorage(questionId.toString());
	} else {
		questionId = getQuestionIdFromLocalStorage();
	}
	return questionId;
};

const getQuestionIdFromUrl = (location: any): number => {
	const match = createFullUrl(location).match(questionPageUrlRegExp);
	if (match) return safeParseInt(match[1]);
	return null;
};

const createFullUrl = (location: any) => `${location.pathname}${location.search}`;
